import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Dropdown, { DropdownOption } from '../../shared/TailwindComponents/Dropdown';
import Toggle from '../../shared/TailwindComponents/Toggle';
import DateTimePicker from '../../shared/TailwindComponents/DatePicker';
import FieldShell, { FIELD_LABEL_CLASS, FIELD_HELP_CLASS } from '../../shared/TailwindComponents/FieldShell';
import UserCell from '../economics/UserCell';
import {
  TimeEntryValue,
  emptyTimeEntry,
  getTimeEntryErrors,
} from '../economics/economicsConstants';
import { ymdToDate, dateToYmd } from '../economics/dateHelpers';
import type { ActivityType, TimeEntry } from '../../../types/economics';
import type { TimeEntryWritePayload } from '../../../store/actions/economicsActions';

interface EditTimeEntryModalProps {
  timeEntry?: TimeEntry | null;
  activityTypes: ActivityType[];
  /** Matter people (collaborators + owner) for the "logged for" picker (value = user_uuid). */
  collaborators?: DropdownOption[];
  /**
   * Matters to log against (value = matter uuid). Given only when the entry is
   * NOT already matter-scoped - i.e. the global "My time" add flow, which has
   * no matter context. When present the modal shows a required matter picker
   * and includes the chosen `matter_uuid` on the saved payload.
   */
  matters?: DropdownOption[];
  /** Pre-selected collaborator UUID (usually the current user). */
  defaultUserUuid?: string;
  onSave: (payload: TimeEntryWritePayload) => Promise<void>;
  onAfterSave?: () => Promise<void> | void;
  /** Reports whether a user (user_uuid) is set, so the host can disable Save. */
  onValidityChange?: (valid: boolean) => void;
}

export interface EditTimeEntryModalHandle {
  handleSave: () => Promise<void>;
  isSaving: boolean;
}

const todayStr = () => new Date().toISOString().slice(0, 10);

/**
 * Add / edit a manual time entry (US #2237, task #2265). Validates
 * 0 < hours <= 24 and a non-future date. Rendered via GlobalModal.
 */
const EditTimeEntryModal = forwardRef<EditTimeEntryModalHandle, EditTimeEntryModalProps>(
  ({ timeEntry, activityTypes, collaborators = [], matters = [], defaultUserUuid, onSave, onAfterSave, onValidityChange }, ref) => {
    const { t } = useTranslation();
    const [value, setValue] = useState<TimeEntryValue>(emptyTimeEntry(todayStr()));
    // Binds to user_uuid (picker option value), NOT the numeric user_id.
    const [userUuid, setUserUuid] = useState<string>(defaultUserUuid || '');
    // Which matter to log against - only used in the global add flow.
    const [matterUuid, setMatterUuid] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<{ entry_date?: string; hours?: string }>({});
    const needsMatter = matters.length > 0 && !timeEntry;

    useEffect(() => {
      if (timeEntry) {
        setValue({
          entry_date: timeEntry.entry_date || todayStr(),
          hours: String(timeEntry.hours_decimal ?? ''),
          activity_type_id: timeEntry.activity_type_id != null ? String(timeEntry.activity_type_id) : '',
          billable: timeEntry.billable ?? true,
          description: timeEntry.description || '',
        });
        setUserUuid(String(timeEntry.user_uuid ?? defaultUserUuid ?? ''));
      } else {
        setUserUuid(defaultUserUuid || '');
      }
    }, [timeEntry, defaultUserUuid]);

    const set = (patch: Partial<TimeEntryValue>) => setValue((p) => ({ ...p, ...patch }));

    const activityOptions = activityTypes.map((a) => ({ value: String(a.id), label: t(a.label) }));

    // Create requires a user_uuid; edit does not reassign the logger. The
    // global add flow also requires a matter to log against.
    const isValid = (!!timeEntry || !!userUuid) && (!needsMatter || !!matterUuid);
    useEffect(() => {
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    const handleSave = async () => {
      const found = getTimeEntryErrors(value, todayStr());
      setErrors(found);
      if (Object.keys(found).length > 0) {
        throw new Error('validation'); // keep GlobalModal open
      }
      setIsSaving(true);
      try {
        await onSave({
          entry_date: value.entry_date,
          hours_decimal: Number(value.hours),
          activity_type_id: value.activity_type_id ? Number(value.activity_type_id) : null,
          billable: value.billable,
          description: value.description.trim() || undefined,
          // Send user_uuid on create; PATCH does not reassign the logger.
          ...(!timeEntry && userUuid ? { user_uuid: userUuid } : {}),
          // The global add flow carries the chosen matter so the host knows
          // which matter to POST the entry to.
          ...(needsMatter && matterUuid ? { matter_uuid: matterUuid } : {}),
        } as TimeEntryWritePayload);
        if (onAfterSave) await onAfterSave();
      } finally {
        setIsSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({ handleSave, isSaving }));

    return (
      <div className="px-6 space-y-5 w-[34rem] max-w-[90vw]">
        {needsMatter && (
          <Dropdown
            id="time-entry-matter"
            label={t('Matter')}
            options={matters}
            value={matterUuid}
            placeholder={t('Select a matter')}
            onChange={(v) => setMatterUuid(Array.isArray(v) ? v[0] : v)}
            required
            fullWidth
            className="w-full"
          />
        )}
        {timeEntry && (
          <FieldShell label={t('Logged by')} translate={false}>
            <UserCell user={timeEntry} fallbackLabel={`User ${timeEntry.user_id}`} />
          </FieldShell>
        )}
        {collaborators.length > 0 && (
          <Dropdown
            id="time-entry-user"
            label={t('Collaborator')}
            options={collaborators}
            value={userUuid}
            placeholder={t('Select a collaborator')}
            onChange={(v) => setUserUuid(Array.isArray(v) ? v[0] : v)}
            fullWidth
            className="w-full"
          />
        )}
        <div className="grid grid-cols-2 gap-4">
          <DateTimePicker
            id="time-entry-date"
            label={t('Date')}
            selected={ymdToDate(value.entry_date)}
            placeholder={t('Date')}
            showTimeSelect={false}
            dateFormat="yyyy-MM-dd"
            maxDate={new Date()}
            fullWidth
            error={errors.entry_date ? t(errors.entry_date) : undefined}
            required
            onChange={(d) => set({ entry_date: dateToYmd(d) })}
          />
          <Input
            type="number"
            id="time-entry-hours"
            label={t('Hours')}
            placeholder="0.00"
            min={0}
            value={value.hours}
            onChange={(e) => set({ hours: e.target.value })}
            error={errors.hours ? t(errors.hours) : undefined}
            required
          />
        </div>

        <Dropdown
          id="time-entry-activity"
          label={t('Activity')}
          options={activityOptions}
          value={value.activity_type_id}
          placeholder={t('Select an activity')}
          onChange={(v) => set({ activity_type_id: Array.isArray(v) ? v[0] : v })}
          fullWidth
          className="w-full"
        />

        <div className="flex items-center justify-between">
          <div>
            <p className={FIELD_LABEL_CLASS}>{t('Billable')}</p>
            <p className={FIELD_HELP_CLASS}>
              {t('Counts toward billable revenue for this matter.')}
            </p>
          </div>
          <Toggle enabled={value.billable} onToggle={(billable) => set({ billable })} />
        </div>

        <Textarea
          id="time-entry-description"
          label={t('Description')}
          value={value.description}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => set({ description: e.target.value })}
          placeholder={t('What did you work on?')}
          rows={3}
        />
      </div>
    );
  },
);

EditTimeEntryModal.displayName = 'EditTimeEntryModal';

export default EditTimeEntryModal;
